import { useCurrentCommunityId } from '@communities/community/hooks';
import type { PanelFilters } from '@content-search/domain';
import { useMetadataFilters } from '@content-search/hooks';
import { validateCustomMetadata } from '@content-search/utils';
import { MultiSelect } from '@soteria/design-system-ve';
import { useMemo, useCallback, useEffect } from 'react';

import * as styles from './FilterPanel.css';

interface MetadataFilterProps {
  isLoading: boolean;
  panelFilters: PanelFilters;
  updateFilters: (updates: Partial<PanelFilters>) => void;
}

const getMetadataOptions = (metadataKeys: Record<string, string[]>) => {
  const options: Array<{ id: string; label: string; value: string; group: string }> = [];

  Object.entries(metadataKeys).forEach(([key, values]) => {
    values.forEach(value => {
      options.push({
        id: `${key}:${value}`,
        label: value, // Show just the value in dropdown options
        value: `${key}:${value}`,
        group: key, // Group will be used to format badges as "key: value"
      });
    });
  });

  return options;
};

// Convert metadata object to selected values format
const getSelectedMetadataValues = (metadata: Record<string, string> = {}) => Object.entries(metadata).map(([key, value]) => `${key}:${value}`);

export function MetadataFilter({ isLoading, panelFilters, updateFilters }: MetadataFilterProps) {
  const communityId = useCurrentCommunityId();
  const { metadataFilters, isFetchingMetadataFilters, getMetadataFilters } = useMetadataFilters();

  // Fetch metadata when communityId changes
  useEffect(() => {
    if (communityId) {
      getMetadataFilters(communityId);
    }
  }, [communityId, getMetadataFilters]);

  const metadataOptions = useMemo(() => getMetadataOptions(metadataFilters.metadataKeys), [metadataFilters.metadataKeys]);
  const selectedMetadataValues = useMemo(() => getSelectedMetadataValues(panelFilters.metadata), [panelFilters.metadata]);

  const handleMetadataChange = useCallback(
    (selectedMetadata: string[]) => {
      const newMetadata: Record<string, string> = {};

      selectedMetadata.forEach(item => {
        const [key, value] = item.split(':');

        if (key && value) {
          newMetadata[key] = value;
        }
      });

      updateFilters({
        metadata: Object.keys(newMetadata).length > 0 ? newMetadata : undefined,
      });
    },
    [updateFilters]
  );

  const handleValidateCustomMetadata = useCallback((input: string) => validateCustomMetadata(input, selectedMetadataValues), [selectedMetadataValues]);

  const handleCustomMetadataCreate = useCallback(
    (key: string, value: string) => {
      const currentMetadata = panelFilters.metadata || {};
      const newMetadata = {
        ...currentMetadata,
        [key]: value,
      };

      updateFilters({ metadata: newMetadata });
    },
    [panelFilters.metadata, updateFilters]
  );

  return (
    <div className={styles.filterSection}>
      <div className={styles.filterLabel}>Metadata Filters</div>
      <MultiSelect
        allowCustomCreate
        searchable
        disabled={isLoading || isFetchingMetadataFilters}
        emptyMessage={isFetchingMetadataFilters ? 'Loading metadata...' : 'No metadata available'}
        options={metadataOptions}
        placeholder={isFetchingMetadataFilters ? 'Loading...' : 'All'}
        searchPlaceholder="Search metadata or add custom key: value..."
        selectedValues={selectedMetadataValues}
        validateCustomOption={handleValidateCustomMetadata}
        onCustomOptionCreate={handleCustomMetadataCreate}
        onSelectionChange={handleMetadataChange}
      />
    </div>
  );
}
